import { z } from 'zod';

import { businessVerificationStatusSchema } from './business';

/**
 * Runtime shape of the API's user payloads. Web-owned by design (spec §3.1):
 * the generated OpenAPI types describe what the API *says* it returns, these
 * validate what it actually returned.
 */
export const publicUserSchema = z.object({
  accountType: z.enum(['PERSONAL', 'BUSINESS']),
  /** Set when this account speaks for a business — carries the verified badge. */
  business: z
    .object({
      slug: z.string(),
      verificationStatus: businessVerificationStatusSchema,
    })
    .nullable(),
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  bio: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  homeTown: z.string(),
  homeCountryCode: z.string(),
  role: z.enum(['USER', 'MODERATOR', 'ADMIN']),
  createdAt: z.string(),
});

export const privateUserSchema = publicUserSchema.extend({
  accountType: z.enum(['PERSONAL', 'BUSINESS']),
  /** The business this account speaks for, if any. */
  businessSlug: z.string().nullable(),
  /** What the business's plan allows — empty for a personal account. */
  businessCapabilities: z.array(z.string()),
  email: z.string(),
  isEmailVerified: z.boolean(),
  status: z.enum(['ACTIVE', 'SUSPENDED', 'DELETED']),
  showAddressPublicly: z.boolean(),
  digestOptIn: z.boolean(),
  canChangeUsername: z.boolean(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  town: z.string(),
  region: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string(),
});

export const authSessionSchema = z.object({
  user: privateUserSchema,
  /** Only when this call created a business — where to send them next. */
  businessSlug: z.string().optional(),
});

/**
 * What a signup answers with.
 *
 * A free plan answers with a session, because the account exists. A paid one
 * answers with somewhere to pay, because it does not yet — and the client has
 * to be able to tell the two apart rather than guessing from a missing field.
 */
export const registerResultSchema = z.union([
  authSessionSchema,
  z.object({ checkoutUrl: z.string() }),
]);

export const acknowledgementSchema = z.object({ message: z.string() });

export type RegisterResult = z.infer<typeof registerResultSchema>;

/** A signup that has to be paid for before it becomes an account. */
export function needsCheckout(result: RegisterResult): result is { checkoutUrl: string } {
  return 'checkoutUrl' in result;
}

export type PublicUser = z.infer<typeof publicUserSchema>;
export type PrivateUser = z.infer<typeof privateUserSchema>;
